"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, LABEL } from "@/components/tool-kit/styles";
import { OutlineNumbererResults } from "@/components/tools/qualitative-text/outline-numberer-results";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  DEFAULT_OUTLINE_OPTIONS,
  definedLevels,
  numberOutline,
  OUTLINE_STYLES,
  type OutlineIndent,
  type OutlineOptions,
  type OutlineStyle,
} from "@/lib/tool-kit/qualitative-text/outline-number";

/** English strings. A translated content file overrides these through `ui`. */
export const OUTLINE_NUMBERER_STRINGS = {
  privacy:
    "Runs entirely in your browser. Your outline and any file you open are never uploaded.",
  inputLabel: "Your outline, one item per line, indented to show depth",
  inputPlaceholder:
    "Introduction\n  Background\n  Aims\nMethods\n  Design\nResults",
  fileLabel: "Or open a text file",
  sample: "Load a sample",
  clear: "Clear",
  styleLabel: "Numbering style",
  styles: {
    decimal: "Decimal: 1, 1.1, 1.1.1",
    alpha: "Outline: I. A. 1. a. (1) (a) (i)",
    legalClause: "Legal clauses: 1. 1.1 (a) (i) (A)",
    legalArticle: "Articles and sections: Article I, Section 1.01, (a) (i) (A)",
    legalStatute: "Statute: Sec. 1. (a) (1) (A) (i) (I) (aa) (AA)",
  } as Record<OutlineStyle, string>,
  styleHints: {
    decimal:
      "Each number repeats its parents' numbers, so any item can be cited on its own. Common in reports, theses and standards.",
    alpha:
      "The traditional outline taught for essays and speeches. Seven levels are defined; a deeper outline starts the pattern again.",
    legalClause:
      "A common pattern for contract clauses. Firms and jurisdictions differ, so check a house style where there is one.",
    legalArticle:
      "For agreements and bylaws organized in articles. Each section carries its article number: Section 2.03 is the third section of Article II.",
    legalStatute:
      "The sequence used in U.S. federal bills and statutes: section, subsection, paragraph, subparagraph, clause, subclause, item and subitem.",
  } as Record<OutlineStyle, string>,
  indentLabel: "Indent the numbered outline by",
  indents: {
    none: "Nothing (flat list)",
    two: "Two spaces per level",
    four: "Four spaces per level",
    tab: "One tab per level",
  } as Record<OutlineIndent, string>,
  tabWidthLabel: "A tab in your outline counts as this many spaces",
  startLabel: "First top-level number",
  trailingDot: "Put a full stop after each number (1. and 1.1.)",
  stripLabels:
    "Remove numbers already on each line (1. a) (ii) Section 2.01). A line that begins like “A. Smith” will lose its first word.",
  wordArticle: "Article",
  wordSection: "Section",
  wordSec: "Sec.",
  emptyPrompt: "Type or paste an outline to number it.",
  results: "Numbered outline",
  noItems: "There are no lines to number.",
  statItems: "Items",
  statDepth: "Levels deep",
  statTop: "Top-level items",
  wrappedNotice:
    "one: This style defines one level, so deeper levels start its pattern again.|other: This style defines {levels} levels, so deeper levels start its pattern again.",
  snappedNotice:
    "one: {count} line was indented between two levels and was placed under the nearest one. Check its position.|other: {count} lines were indented between two levels and were placed under the nearest one. Check their positions.",
  outputLabel: "Numbered outline",
  copy: "Copy",
  copied: "Copied!",
  download: "Download .txt",
  filename: "numbered-outline.txt",
  levelsHeading: "Numbering by level",
  colLevel: "Level",
  colFirst: "First label",
  colItems: "Items",
  sampleText:
    "Introduction\n  Background to the study\n  Research questions\n    Primary question\n    Secondary questions\nLiterature review\n  Theoretical framework\nMethods\n  Participants\n  Data collection\nResults\nDiscussion",
} as const;

export default function OutlineNumbererWidget() {
  const strings = useToolUi(OUTLINE_NUMBERER_STRINGS);
  const [text, setText] = useState("");
  const [style, setStyle] = useState<OutlineStyle>(
    DEFAULT_OUTLINE_OPTIONS.style
  );
  const [indent, setIndent] = useState<OutlineIndent>(
    DEFAULT_OUTLINE_OPTIONS.indent
  );
  const [tabWidth, setTabWidth] = useState("4");
  const [start, setStart] = useState("1");
  const [trailingDot, setTrailingDot] = useState(false);
  const [stripLabels, setStripLabels] = useState(false);
  const deferred = useDeferredValue(text);

  const options: OutlineOptions = useMemo(
    () => ({
      style,
      indent,
      tabWidth: Math.max(1, parseIntegerInput(tabWidth) ?? 4),
      start: Math.max(1, parseIntegerInput(start) ?? 1),
      trailingDot,
      stripLabels,
      words: {
        article: strings.wordArticle,
        section: strings.wordSection,
        sec: strings.wordSec,
      },
    }),
    [
      style,
      indent,
      tabWidth,
      start,
      trailingDot,
      stripLabels,
      strings.wordArticle,
      strings.wordSection,
      strings.wordSec,
    ]
  );
  const result = useMemo(
    () => (deferred.trim() ? numberOutline(deferred, options) : null),
    [deferred, options]
  );

  async function openFile(file: File | undefined) {
    if (file) {
      setText(await file.text());
    }
  }

  return (
    <>
      <Notice>{strings.privacy}</Notice>
      <TextAreaField
        id="on-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sampleText)}
          type="button"
        >
          {strings.sample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
        <label className={LABEL} htmlFor="on-file">
          {strings.fileLabel}
        </label>
        <input
          accept=".txt,.md,.markdown,text/*"
          className="text-foreground text-sm"
          id="on-file"
          onChange={(event) => {
            openFile(event.target.files?.[0]);
            event.target.value = "";
          }}
          type="file"
        />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <SelectField
          hint={strings.styleHints[style]}
          id="on-style"
          label={strings.styleLabel}
          onChange={setStyle}
          options={OUTLINE_STYLES.map((value) => ({
            value,
            label: strings.styles[value],
          }))}
          value={style}
        />
        <SelectField
          id="on-indent"
          label={strings.indentLabel}
          onChange={setIndent}
          options={(Object.keys(strings.indents) as OutlineIndent[]).map(
            (value) => ({ value, label: strings.indents[value] })
          )}
          value={indent}
        />
        <TextField
          id="on-start"
          label={strings.startLabel}
          numeric
          onChange={setStart}
          value={start}
        />
        <TextField
          id="on-tab"
          label={strings.tabWidthLabel}
          numeric
          onChange={setTabWidth}
          value={tabWidth}
        />
      </div>
      <div className="space-y-2">
        {style === "decimal" ? (
          <Checkbox
            checked={trailingDot}
            label={strings.trailingDot}
            onChange={setTrailingDot}
          />
        ) : null}
        <Checkbox
          checked={stripLabels}
          label={strings.stripLabels}
          onChange={setStripLabels}
        />
      </div>
      {result ? (
        <OutlineNumbererResults
          definedLevels={definedLevels(style)}
          result={result}
          strings={strings}
        />
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
